import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Add_StageType_Action } from '../../Redux/Actions/StageTypeAction';
import { Notify } from '../../Utility/Notify';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconPlus   = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconTag    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const IconNote   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;

/* ─── Field Label ────────────────────────────────────────────────────────── */
const FieldLabel = ({ icon, label, required, isDark }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
    <span style={{ color: isDark ? '#4b5563' : '#94a3b8', display: 'flex' }}>{icon}</span>
    <span style={{
      fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: isDark ? '#9ca3af' : '#6b7280',
    }}>
      {label}
    </span>
    {required && <span style={{ color: '#ef4444', fontSize: '14px', lineHeight: 1 }}>*</span>}
  </div>
);

/* ─── Input Style ────────────────────────────────────────────────────────── */
const inputStyle = (isDark, focused) => ({
  width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px',
  border: `1.5px solid ${focused ? '#2563eb' : (isDark ? '#2d3248' : '#e2e8f0')}`,
  backgroundColor: isDark ? '#151827' : '#f8fafc',
  color: isDark ? '#e5e7eb' : '#1f2937',
  fontFamily: "'DM Sans', sans-serif",
  outline: 'none', boxSizing: 'border-box', transition: 'all 0.15s',
  boxShadow: focused ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
});

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const AddNewType = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [userlogin,    setUserlogin]    = useState(null);
  const [name,         setName]         = useState('');
  const [comment,      setComment]      = useState('');
  const [loading,      setLoading]      = useState(true);
  const [addLoading,   setAddLoading]   = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const res_add = useSelector((state) => state.AllStagetype.stagetype);

  const homeRoute =
    userlogin?.data?.role === 'Admin'      ? '/AllLeadsForAdmin'
  : userlogin?.data?.role === 'Marketer'   ? '/ALLLeadsForMarketer'
  : userlogin?.data?.role === 'Accountant' ? '/LeadsForAccountant'
  : '/';

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      Notify('error', 'Type Name is required', 'error');
      return;
    }
    setLoading(true);
    setAddLoading(true);
    await dispatch(Add_StageType_Action({ name, Comment: comment }));
    setLoading(false);
    setAddLoading(false);
  };

  useEffect(() => {
    if (loading === false) {
      if (res_add?.status === 201) {
        Notify('success', 'Added successfully', 'success');
        setName('');
        setComment('');
      } else {
        Notify('error', 'Failed to add type', 'error');
        setTimeout(() => window.location.reload(), 1000);
      }
    }
  }, [loading]);

  const previewInitials = name?.trim().slice(0, 2).toUpperCase() || 'TY';

  return (
    <>
      <style>{`
        @keyframes st-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes st-spin     { to   { transform: rotate(360deg); } }
        .st-add-card { animation: st-fade-in 0.3s ease; }
      `}</style>

      <div style={{
        position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        backgroundColor: isDark ? '#0f1117' : '#f0f2f5',
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>

        {/* ── Breadcrumb ── */}
        <div style={{
          padding: '14px 28px', flexShrink: 0,
          backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        }}>
          <Link to={homeRoute} style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Home
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <Link to="/Stages" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Stages
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Add New Type</span>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 28px' }}>
          <div style={{ maxWidth: '680px' }}>

            {/* Page Title */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: isDark ? '#4b5563' : '#94a3b8', marginBottom: '4px',
              }}>
                PIPELINE
              </div>
              <h1 style={{
                fontSize: '26px', fontWeight: 800, margin: 0,
                color: isDark ? '#f9fafb' : '#111827', letterSpacing: '-0.5px',
              }}>
                Add New Stage Type
              </h1>
            </div>

            {/* ── Main Card ── */}
            <div className="st-add-card" style={{
              backgroundColor: isDark ? '#1a1d2e' : '#ffffff',
              border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
              borderRadius: '20px',
              boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.07)',
              overflow: 'hidden',
            }}>

              {/* Card Header — Live Preview */}
              <div style={{
                padding: '18px 24px',
                borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f3f8'}`,
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{
                  width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #0D47A1, #1565C0)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', fontWeight: 800, color: '#fff',
                  transition: 'all 0.2s',
                }}>
                  {previewInitials}
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: isDark ? '#f3f4f6' : '#111827' }}>
                    {name || 'New Type'}
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#9ca3af', marginTop: '1px' }}>
                    Stage Type
                  </div>
                </div>
              </div>

              {/* Form Body */}
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>

                  {/* Type Name */}
                  <div>
                    <FieldLabel icon={<IconTag />} label="Type Name" required isDark={isDark} />
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                        color: isDark ? '#4b5563' : '#94a3b8', display: 'flex', pointerEvents: 'none',
                      }}>
                        <IconTag />
                      </span>
                      <input
                        type="text"
                        placeholder="e.g. Pre-Sale, Post-Sale…"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        style={{ ...inputStyle(isDark, focusedField === 'name'), paddingLeft: '42px' }}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <FieldLabel icon={<IconNote />} label="Notes" isDark={isDark} />
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute', left: '14px', top: '14px',
                        color: isDark ? '#4b5563' : '#94a3b8', display: 'flex', pointerEvents: 'none',
                      }}>
                        <IconNote />
                      </span>
                      <textarea
                        placeholder="Add any notes or comments about this type…"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        onFocus={() => setFocusedField('notes')}
                        onBlur={() => setFocusedField(null)}
                        rows={4}
                        style={{
                          ...inputStyle(isDark, focusedField === 'notes'),
                          paddingLeft: '42px', resize: 'vertical', lineHeight: '1.6',
                        }}
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Card Footer — Actions */}
              <div style={{
                padding: '16px 24px',
                borderTop: `1px solid ${isDark ? '#2a2f45' : '#f1f3f8'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px',
              }}>
                <Link to="/Stages" style={{ textDecoration: 'none' }}>
                  <button
                    style={{
                      padding: '11px 24px', borderRadius: '12px', border: 'none',
                      cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                      fontFamily: "'DM Sans',sans-serif",
                      background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                      color: isDark ? '#d1d5db' : '#374151', transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'; }}
                  >
                    Cancel
                  </button>
                </Link>

                <button
                  onClick={!addLoading ? onSubmit : undefined}
                  disabled={addLoading}
                  style={{
                    padding: '11px 28px', borderRadius: '12px', border: 'none',
                    cursor: addLoading ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 700,
                    fontFamily: "'DM Sans',sans-serif",
                    background: addLoading
                      ? (isDark ? 'rgba(37,99,235,0.5)' : 'rgba(37,99,235,0.55)')
                      : 'linear-gradient(135deg, #0D47A1, #1565C0)',
                    color: '#fff', transition: 'all 0.2s',
                    boxShadow: addLoading ? 'none' : '0 4px 14px rgba(37,99,235,0.4)',
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    opacity: addLoading ? 0.75 : 1,
                  }}
                  onMouseEnter={(e) => { if (!addLoading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {addLoading ? (
                    <>
                      <span style={{
                        width: '15px', height: '15px', borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                        animation: 'st-spin 0.7s linear infinite', display: 'inline-block',
                        flexShrink: 0,
                      }} />
                      Adding…
                    </>
                  ) : (
                    <>
                      <IconPlus />
                      Add Type
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Tip */}
            <div style={{
              marginTop: '16px', padding: '12px 16px', borderRadius: '12px', fontSize: '12px',
              background: isDark ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.05)',
              border: `1px solid ${isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.15)'}`,
              color: isDark ? '#93c5fd' : '#0D47A1',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '14px' }}>💡</span>
              <span>Type name is required. Notes are optional but help describe the purpose of this type.</span>
            </div>

          </div>
        </div>

        {/* Footer */}
        <footer style={{ padding: '14px 28px', flexShrink: 0, fontSize: '12px', color: isDark ? '#374151' : '#d1d5db' }}>
          © 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.
        </footer>
      </div>
    </>
  );
};